"use client"

import type React from "react"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { api, type Product } from "@/lib/api"
import { Phone, User, Package, CheckCircle, Loader2, ShoppingCart } from "lucide-react"
import { useLanguage } from "@/lib/language-context"

interface OrderModalProps {
  product: Product
  isOpen: boolean
  onClose: () => void
}

export function OrderModal({ product, isOpen, onClose }: OrderModalProps) {
  const { t } = useLanguage()
  const [customerName, setCustomerName] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState("")

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, "")

    // Limit to 12 digits (998 + 9 digits)
    const limited = digits.slice(0, 12)

    // Format as +998 XX XXX XX XX
    if (limited.length <= 3) {
      return limited
    } else if (limited.length <= 5) {
      return `+${limited.slice(0, 3)} ${limited.slice(3)}`
    } else if (limited.length <= 8) {
      return `+${limited.slice(0, 3)} ${limited.slice(3, 5)} ${limited.slice(5)}`
    } else if (limited.length <= 10) {
      return `+${limited.slice(0, 3)} ${limited.slice(3, 5)} ${limited.slice(5, 8)} ${limited.slice(8)}`
    } else {
      return `+${limited.slice(0, 3)} ${limited.slice(3, 5)} ${limited.slice(5, 8)} ${limited.slice(8, 10)} ${limited.slice(10)}`
    }
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value)
    setPhoneNumber(formatted)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")

    // Validate phone
    const digits = phoneNumber.replace(/\D/g, "")
    if (digits.length < 12) {
      setError("Telefon raqamini to'liq kiriting")
      return
    }

    setIsLoading(true)

    const result = await api.createOrder({
      product: product.id,
      customer_name: customerName,
      phone_number: phoneNumber,
      quantity: 1,
      note: "",
    })

    setIsLoading(false)

    if (result.success) {
      setIsSuccess(true)
      setTimeout(() => {
        onClose()
        setIsSuccess(false)
        setCustomerName("")
        setPhoneNumber("")
      }, 2000)
    } else {
      setError(result.error || "Xatolik yuz berdi")
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      onClose()
      setError("")
      setIsSuccess(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[420px] p-0 overflow-hidden bg-white dark:bg-gray-900 border-0 shadow-2xl">
        {isSuccess ? (
          <div className="flex flex-col items-center justify-center py-12 px-6">
            <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-green-500 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-green-500/30">
              <CheckCircle className="h-10 w-10 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{t("home", "order_success") || "Buyurtma qabul qilindi!"}</h3>
            <p className="text-muted-foreground mt-3 text-center">
              {t("home", "order_success_message") || "Tez orada siz bilan bog'lanamiz"}
            </p>
          </div>
        ) : (
          <>
            {/* Header with gradient */}
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 px-6 py-5">
              <DialogHeader className="text-white">
                <DialogTitle className="flex items-center gap-3 text-xl text-white">
                  <ShoppingCart className="h-6 w-6" />
                  {t("home", "order_modal_title") || "Buyurtma berish"}
                </DialogTitle>
                <DialogDescription className="text-emerald-100 mt-1">
                  {product.name}
                </DialogDescription>
              </DialogHeader>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Name Input */}
              <div className="space-y-2">
                <Label htmlFor="name" className="flex items-center gap-2 text-sm font-medium">
                  <User className="h-4 w-4 text-emerald-500" />
                  {t("home", "order_name") || "Ismingiz"}
                </Label>
                <Input
                  id="name"
                  placeholder="Ismingizni kiriting"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  required
                  disabled={isLoading}
                  className="h-12 bg-slate-50 dark:bg-gray-800 border-slate-200 dark:border-gray-700 focus:border-emerald-500 focus:ring-emerald-500 rounded-xl"
                />
              </div>

              {/* Phone Input */}
              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-2 text-sm font-medium">
                  <Phone className="h-4 w-4 text-emerald-500" />
                  {t("home", "order_phone") || "Telefon raqam"}
                </Label>
                <div className="relative">
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+998 90 123 45 67"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    required
                    disabled={isLoading}
                    className="h-12 bg-slate-50 dark:bg-gray-800 border-slate-200 dark:border-gray-700 focus:border-emerald-500 focus:ring-emerald-500 rounded-xl pl-4 text-base tracking-wide"
                  />
                </div>
              </div>

              {/* Product info display */}
              <div className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-gray-800 dark:to-gray-750 p-4 rounded-xl border border-slate-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  {product.image_url && (
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-14 h-14 rounded-lg object-cover"
                    />
                  )}
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-white">{product.name}</p>
                    <p className="text-sm text-muted-foreground">1 dona</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold bg-gradient-to-r from-emerald-500 to-teal-600 bg-clip-text text-transparent">
                      {Number(product.price).toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground">so'm</p>
                  </div>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-xl text-sm text-center">
                  {error}
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 h-12 rounded-xl border-slate-300 dark:border-gray-600 hover:bg-slate-100 dark:hover:bg-gray-800"
                  onClick={handleClose}
                  disabled={isLoading}
                >
                  {t("home", "order_cancel") || "Bekor qilish"}
                </Button>
                <Button
                  type="submit"
                  className="flex-1 h-12 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-semibold shadow-lg shadow-emerald-500/30"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Yuborilmoqda...
                    </>
                  ) : (
                    t("home", "order_submit") || "Buyurtma berish"
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
